import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout, isSuperuser, getMe } from '../api/auth';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['data']));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (group: string) => {
    const next = new Set(expandedGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    setExpandedGroups(next);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const ic = (paths: React.ReactNode) => (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
      {paths}
    </svg>
  );
  const rl = 'round' as const;

  const iconDashboard = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />);
  const iconWidget = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />);
  // Official WhatsApp glyph (monochrome in the sidebar; brand green lives on
  // the WhatsApp page itself — icon set stays one color).
  const iconWhatsApp = (
    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
  const iconDocs = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />);
  const iconPreview = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />);
  const iconSettings = ic(<><path strokeLinecap={rl} strokeLinejoin={rl} d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path strokeLinecap={rl} strokeLinejoin={rl} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>);
  const iconChats = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242M20.25 8.511c-.215-.79-.916-1.402-1.79-1.477-2.79-.198-5.6-.198-8.39 0-1.094.092-1.95.92-2.07 2.015a45.806 45.806 0 00-.072 8.105c.119 1.09.974 1.917 2.07 2.015 1.327.108 2.667.163 4.02.163m-4.5-12.214c-.215-.79-.916-1.402-1.79-1.477-2.79-.198-5.6-.198-8.39 0a2.115 2.115 0 00-2.07 2.015 45.85 45.85 0 00-.072 8.105c.119 1.09.974 1.917 2.07 2.015.34.027.68.052 1.02.072v3.091l3-3c1.354 0 2.694-.055 4.02-.163" />);
  const iconFeedback = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />);
  const iconGaps = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />);
  const iconUsers = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />);
  const iconExternal = ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />);

  interface NavGroup { label: string; items: (NavItem & { external?: boolean })[] }
  const su = isSuperuser();

  const navGroups: NavGroup[] = [
    {
      label: 'Platform',
      items: [
        { label: 'Dashboard', path: '/', icon: iconDashboard },
        { label: 'Chat Widget', path: '/chat-widget', icon: iconWidget },
        ...(su ? [{ label: 'WhatsApp', path: '/whatsapp', icon: iconWhatsApp }] : []),
        { label: 'Preview answers', path: '/retrieval-test', icon: iconPreview },
      ],
    },
    {
      label: 'Content',
      items: [
        { label: 'Domains', path: '/data/domains', icon: ic(<path strokeLinecap={rl} strokeLinejoin={rl} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />) },
        { label: 'Documents', path: '/documents', icon: iconDocs },
      ],
    },
    ...(su ? [{
      label: 'Insights',
      items: [
        { label: 'Chats', path: '/chats', icon: iconChats },
        { label: 'Feedback', path: '/feedback', icon: iconFeedback },
        { label: 'Content Gaps', path: '/content-gaps', icon: iconGaps },
      ],
    }] : []),
    {
      label: 'System',
      items: [
        ...(su ? [{ label: 'Settings', path: '/settings', icon: iconSettings }] : []),
        ...(su ? [{ label: 'Users', path: '/users', icon: iconUsers }] : []),
        { label: 'Widget Demo', path: '/demo', icon: iconExternal, external: true },
      ],
    },
  ];

  const dataSubItems = [
    { label: 'Overview', path: '/data' },
    { label: 'Faculties', path: '/data/faculties' },
    { label: 'Programs', path: '/data/programs' },
    { label: 'Contacts', path: '/data/contacts' },
    { label: 'Departments', path: '/data/departments' },
    { label: 'Admission Grades', path: '/data/admission-grades' },
  ];

  // Build breadcrumbs from path
  const buildBreadcrumbs = () => {
    const path = location.pathname;
    const crumbs: { label: string; path?: string }[] = [];

    if (path === '/') return [{ label: 'Dashboard' }];

    const segments = path.split('/').filter(Boolean);
    const labelMap: Record<string, string> = {
      'documents': 'Documents',
      'create': 'Create',
      'retrieval-test': 'Preview answers',
      'chat-widget': 'Chat Widget',
      'whatsapp': 'WhatsApp',
      'settings': 'Settings',
      'users': 'Users',
      'chats': 'Chats',
      'feedback': 'Feedback',
      'content-gaps': 'Content Gaps',
      'data': 'Data Management',
      'domains': 'Domains',
      'faculties': 'Faculties',
      'programs': 'Programs',
      'contacts': 'Contacts',
      'departments': 'Departments',
      'admission-grades': 'Admission Grades',
      'edit': 'Edit',
    };

    let accumulated = '';
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      accumulated += '/' + seg;

      // Skip numeric IDs
      if (/^\d+$/.test(seg)) continue;

      const label = labelMap[seg] || seg;
      const isLast = i === segments.length - 1 || (i === segments.length - 2 && /^\d+$/.test(segments[segments.length - 1]));

      if (isLast) {
        crumbs.push({ label });
      } else {
        crumbs.push({ label, path: accumulated });
      }
    }

    return crumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  const navLinkClass = (active: boolean) =>
    `relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors group
     ${active ? 'bg-primary-50 text-primary-800' : 'text-primary-600 hover:bg-primary-50 hover:text-primary-900'}
     ${sidebarCollapsed ? 'justify-center' : ''}`;

  const ActiveRail = () => (
    <span className="absolute inset-y-[7px] start-0 w-[3px] rounded-full bg-primary-500" aria-hidden="true" />
  );

  const navIconClass = (active: boolean) =>
    `flex-shrink-0 ${active ? 'text-primary-700' : 'text-primary-400 group-hover:text-primary-600'}`;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-secondary">
      {/* Sidebar */}
      <aside
        className={`${sidebarCollapsed ? 'w-[64px]' : 'w-60'} flex-shrink-0 bg-white border-e border-primary-200 flex flex-col transition-all ease-in-out`}
      >
        {/* Brand */}
        <div className={`h-14 flex items-center flex-shrink-0 ${sidebarCollapsed ? 'px-3 justify-center' : 'px-4'}`}>
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            <img src="/nnu_logo_color.svg" alt="NNU" className="w-7 h-7 flex-shrink-0" />
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <div className="text-sm font-semibold text-primary-900 truncate leading-none">An-Najah</div>
                <div className="text-xs text-primary-400 mt-1 leading-none">Chatbot Admin</div>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto border-t border-primary-100">
          {navGroups.map((group, gi) => (
            <div key={group.label} className={`${sidebarCollapsed ? 'px-2' : 'px-3'} ${gi > 0 ? 'mt-5' : ''}`}>
              {!sidebarCollapsed
                ? <div className="eyebrow-light px-3 mb-1.5">{group.label}</div>
                : gi > 0 && <div className="hairline my-2" />}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  if (item.external) {
                    return (
                      <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer"
                         className={navLinkClass(false)} title={sidebarCollapsed ? item.label : undefined}>
                        <span className={navIconClass(false)}>{item.icon}</span>
                        {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                      </a>
                    );
                  }
                  const active = isActive(item.path);
                  return (
                    <Link key={item.path} to={item.path} className={navLinkClass(active)}
                          title={sidebarCollapsed ? item.label : undefined}
                          aria-current={active ? 'page' : undefined}>
                      {active && <ActiveRail />}
                      <span className={navIconClass(active)}>{item.icon}</span>
                      {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  );
                })}

                {group.label === 'Content' && (
                  <div>
                    <button
                      onClick={() => sidebarCollapsed ? navigate('/data') : toggleGroup('data')}
                      className={`w-full ${navLinkClass(isActive('/data') && !isActive('/data/domains'))}`}
                      title={sidebarCollapsed ? 'Data Management' : undefined}
                    >
                      <span className={navIconClass(isActive('/data') && !isActive('/data/domains'))}>
                        <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                        </svg>
                      </span>
                      {!sidebarCollapsed && (
                        <>
                          <span className="flex-1 text-left">Data</span>
                          <svg
                            className={`w-3.5 h-3.5 text-primary-400 transition-transform ${expandedGroups.has('data') ? 'rotate-90' : ''}`}
                            fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </>
                      )}
                    </button>
                    {!sidebarCollapsed && expandedGroups.has('data') && (
                      <div className="mt-0.5 ml-[22px] pl-3 border-l border-primary-200 space-y-px py-1">
                        {dataSubItems.map((sub) => {
                          const active = sub.path === '/data'
                            ? location.pathname === '/data'
                            : location.pathname.startsWith(sub.path);
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={`block px-2.5 py-1.5 rounded-md text-sm transition-colors
                                ${active
                                  ? 'text-primary-700 font-medium'
                                  : 'text-primary-500 hover:text-primary-900'
                                }`}
                            >
                              {sub.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className={`border-t border-primary-100 flex-shrink-0 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
          {!sidebarCollapsed && getMe() && (
            <div className="mb-2 px-3 py-2">
              <div className="text-sm text-primary-800 font-medium truncate" title={getMe()?.username}>
                {getMe()?.username}
              </div>
              <div className="text-xs text-primary-400 mt-0.5">
                {getMe()?.is_superuser ? 'Superuser' : `${getMe()?.domains.length} domain(s)`}
              </div>
            </div>
          )}
          <div className={`flex ${sidebarCollapsed ? 'flex-col gap-1' : 'items-center gap-1'}`}>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`flex items-center justify-center p-2 rounded-lg text-primary-400 hover:text-primary-700 hover:bg-primary-50 transition-colors ${sidebarCollapsed ? '' : 'flex-none'}`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg className={`w-4 h-4 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
              </svg>
            </button>
            <button
              onClick={handleLogout}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg text-xs font-medium text-primary-400 hover:text-red-600 hover:bg-red-50 transition-colors ${sidebarCollapsed ? '' : 'flex-1 justify-start px-3'}`}
              title="Sign out"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              {!sidebarCollapsed && <span>Sign out</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 flex-shrink-0 bg-white/80 backdrop-blur border-b border-primary-200 flex items-center px-6 lg:px-8">
          <nav className="flex items-center text-sm min-w-0">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <svg className="w-3.5 h-3.5 text-primary-300 mx-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                )}
                {crumb.path ? (
                  <Link to={crumb.path} className="text-primary-400 hover:text-primary-700 transition-colors truncate">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-primary-800 font-medium truncate">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
